import React, { useEffect, useMemo, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { ssrContentStateManager } from '@confluence/ssr-utilities/entry-points/SSRContentStateManager.ts';

import { LazyEditorContentStateComponent } from '../lazyLoadables.ts';

import { ContentStateStateContainer, defaultValues } from './ContentStateStateContainer.tsx';
import {
	type EditorContentStateQuery$data,
	type EditorContentStateQuery$variables,
} from './__generated__/EditorContentStateQuery.graphql.ts';
import type { EditorContentStateProps } from './types.ts';

const EditorContentStateDataGetter = ({
	contentId,
	contentType,
	contentStateState,
	isPublishingContent,
	userOffline,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
}: EditorContentStateProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCurrentPageLive = useIsCurrentPageLive();

	const { data } = useQueryInterop<EditorContentStateQuery$data, EditorContentStateQuery$variables>(
		graphql`
			query EditorContentStateQuery(
				$contentId: ID!
				$isDraft: Boolean!
				$isApprovalsFgOn: Boolean!
				$format: GraphQLDateFormat!
				$shouldSkipContentStateLastUpdated: Boolean!
			) {
				singleContent(id: $contentId) {
					id
					contentState(isDraft: $isDraft) {
						id
						name
						color
						restrictionLevel
						isCallerPermitted
						lastModifierAaid @include(if: $isApprovalsFgOn)
					}
					# currently only view page and live pages require this field to be loaded for the content header
					# if the Approvals experiment is enabled, we include the content state last updated field
					contentStateLastUpdated(format: $format) @skip(if: $shouldSkipContentStateLastUpdated) {
						value
					}
				}
				availableContentStates(contentId: $contentId) {
					customContentStates {
						name
						color
						id
					}
					spaceContentStates {
						name
						color
						id
						restrictionLevel
					}
				}
			}
		`,
		{
			fetchPolicy: Boolean(window.__SSR_RENDERED__) ? 'cache-first' : 'network-only',
			variables: {
				contentId,
				isDraft: !isCurrentPageLive,
				shouldSkipContentStateLastUpdated: expValEquals(
					'confluence_approvals_eap',
					'isEnabled',
					true,
				)
					? false
					: !isCurrentPageLive,
				isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
				format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
					? 'MILLIS'
					: 'USER_FRIENDLY',
			} as EditorContentStateQuery$variables,
			skip: !contentStateState.state.shouldRunContentStateEffect,
		},
	);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && data) {
		ssrContentStateManager.setContentStateData(data);
	}

	// Memoize filtered arrays to avoid expensive operations during render
	const defaultStatesArray = useMemo(
		() => data?.availableContentStates?.spaceContentStates?.filter((i) => !!i) || [],
		[data?.availableContentStates?.spaceContentStates],
	);
	const customStatesArray = useMemo(
		() => data?.availableContentStates?.customContentStates?.filter((i) => !!i) || [],
		[data?.availableContentStates?.customContentStates],
	);

	// In scenarios where this component stays mounted while the contentId changes (e.g. via seamless transition), we need to reset the contentStateState to prevent a stale content state from being shown
	const prevContentIdRef = useRef(contentId);
	useEffect(() => {
		if (
			prevContentIdRef.current !== contentId &&
			fg('confluence_frontend_transition_preloading_system')
		) {
			prevContentIdRef.current = contentId;
			void contentStateState?.resetToDefaults();
		}
	}, [contentId, contentStateState]);

	useEffect(() => {
		if (data && contentStateState.state.shouldRunContentStateEffect) {
			const newContentStateValues = Object.assign({}, defaultValues);
			/* EditorContentState is rendered in the breadcrumbs & title area content components based on if a page has a content
      state or not. The re-render from the change in parent causes the useEffect and query to run again, resetting the values.
      To prevent this, we have the shouldRunContentStateEffect state. If we are inside the if statement, it means it's the first time
      we're fetching (aka first page load). Since we already ran the effect once, don't do it again since otherwise it
      will cause a values reset. */
			newContentStateValues.shouldRunContentStateEffect = false;
			newContentStateValues.contentState = data?.singleContent?.contentState || null;
			newContentStateValues.contentStateLastUpdated =
				data?.singleContent?.contentStateLastUpdated?.value || null;
			newContentStateValues.defaultStatesArray = defaultStatesArray;
			newContentStateValues.customStatesArray = customStatesArray;
			// If an admin has turned off a toggle, BE returns us the value as null
			// If an empty array is returned [], means the feature is still enabled - there's just no values in there.
			// if both return as null it means the admin disabled content states
			const isSuggestedContentStatesDisabled = !data?.availableContentStates?.spaceContentStates;
			const isCustomContentStatesDisabled = !data?.availableContentStates?.customContentStates;
			const isContentStatesDisabled =
				isSuggestedContentStatesDisabled && isCustomContentStatesDisabled;
			if (isContentStatesDisabled) {
				newContentStateValues.isContentStateEnabled = false;
			} else if (isSuggestedContentStatesDisabled) {
				// if spaceContentStates is null, admin disabled suggested
				newContentStateValues.isSuggestedEnabled = false;
			} else if (isCustomContentStatesDisabled) {
				// if customContentStates is null, admin disabled custom
				newContentStateValues.isCustomEnabled = false;
			}
			void contentStateState.setInitialValues(newContentStateValues);
		}
	}, [data, contentStateState, defaultStatesArray, customStatesArray]);

	if (isPublishingContent) {
		// isPublishingContent is an optional prop that we only pass to this component if a page has a content state.
		// Therefore if it's true, means we're publishing a page with a content state so fire the analytic event
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'publishedWithContentState',
				actionSubject: 'page',
				objectId: contentId,
				source: 'editorContentState',
			},
		}).fire();
	}

	return (
		<LazyEditorContentStateComponent
			contentId={contentId}
			contentType={contentType}
			contentStateState={contentStateState}
			userOffline={userOffline}
			onContentStateMenuToggle={onContentStateMenuToggle}
			floatingToolbarButtonStyle={floatingToolbarButtonStyle}
			renderPlusButtonEmptyState={renderPlusButtonEmptyState}
			isDetailsPanel={isDetailsPanel}
			onClick={onClick}
		/>
	);
};

const EditorContentStateWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(EditorContentStateDataGetter);

const contentStateContainers = [ContentStateStateContainer];

export const EditorContentState = (props: any) => (
	<Subscribe to={contentStateContainers}>
		{(contentStateState: ContentStateStateContainer) => (
			<EditorContentStateWithErrorBoundary {...props} contentStateState={contentStateState} />
		)}
	</Subscribe>
);
